// PRD 10 §3.5 / §3.6 — content Add / Edit slide panel.
//
// Slice 5 (this file) builds the form shell: EID (read-only) · Title · Category
// + the Save Draft / Publish actions, the publish-confirm modal, discard-on-
// dirty-close, validation, and create/update wiring. The content-block editor,
// thumbnail upload, and the live mobile preview arrive in Slice 6 (the block
// region is a placeholder for now).
//
// Save flows:
//   • Save Draft → DRAFT (toast + close).
//   • Publish    → confirm modal ("게시 시 즉시 환자 앱에 노출됩니다…") → PUBLISHED
//                  (toast + close). HE publishes straight to the Patient App —
//                  there is no separate deployment step (unlike PRD 07).
// PRD §3.6 — a PUBLISHED item can't return to DRAFT, so Save Draft is hidden
// when editing a row whose current status is PUBLISHED.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

import {
  useCreateContent,
  useDeleteContent,
  useUpdateContent,
} from '../hooks/use-health-encyclopedia-mutations';
import {
  useCategoriesQuery,
  useContentDetailQuery,
} from '../hooks/use-health-encyclopedia';
import { useHealthEncyclopediaPageStore } from '../store/health-encyclopedia-page.store';
import {
  HealthEncyclopediaApiError,
  type ContentBlock,
  type ContentBlockType,
  type ContentDetail,
  type ContentStatus,
} from '../types/health-encyclopedia.types';
import { getEncyclopediaErrorMessage } from '../utils/error-message';
import {
  MAX_BLOCKS,
  TITLE_MAX,
  validateContentForm,
  withoutEmptyMediaBlocks,
  type ContentFormErrors,
  type ContentFormFieldKey,
} from '../utils/validate-form';
import { EncyclopediaBlockList } from './EncyclopediaBlockList';
import { EncyclopediaMobilePreview } from './EncyclopediaMobilePreview';
import { EncyclopediaThumbnailUpload } from './EncyclopediaThumbnailUpload';

interface FormState {
  title: string;
  categoryId: number | null;
  blocks: ContentBlock[];
  thumbnailUrl: string | null;
}

const EMPTY_FORM: FormState = { title: '', categoryId: null, blocks: [], thumbnailUrl: null };

function formFromDetail(detail: ContentDetail): FormState {
  return {
    title: detail.title,
    categoryId: detail.categoryId,
    blocks: detail.blocks ?? [],
    thumbnailUrl: detail.thumbnailUrl,
  };
}

// Block id — Date.now() plus an in-instance sequence so rapid "Add" clicks
// don't collide (Date.now() resolution is 1ms). Client-only; BE may reassign.
let blockSeq = 0;
function nextBlockId(): number {
  blockSeq += 1;
  return Date.now() + blockSeq;
}

// Mirrors the prototype `addBlock`: image/video carry title + description +
// showText toggle (PRD v2.5 §3.7 block-editor unification); text carries only
// its WYSIWYG body.
function buildBlock(type: ContentBlockType): ContentBlock {
  const base: ContentBlock = { id: nextBlockId(), type, text: '' };
  if (type === 'image' || type === 'video') {
    base.title = '';
    base.showText = false;
  }
  if (type === 'video') base.url = '';
  return base;
}

const FIELD_KEYS: readonly ContentFormFieldKey[] = ['title', 'categoryId', 'blocks'];

export function ContentFormPanel() {
  const { t } = useTranslation('health-encyclopedia');
  // SHCT-582 — PRD 21 §7.4 P-2. The fields were already locked without CONTENT_LIB:WRITE
  // (`fieldsDisabled` below), but the whole footer was withheld, so the panel offered no statement
  // of why it could not be saved. The 삭제 button keeps its own Root-only rule (PRD §3.8) — a
  // different axis, and Root always holds WRITE.
  const { canWrite, denyProps } = useWriteGuard('CONTENT_LIB');
  const isRoot = useAuthStore((s) => s.isRoot);

  const panel = useHealthEncyclopediaPageStore((s) => s.panel);
  const closePanel = useHealthEncyclopediaPageStore((s) => s.closePanel);
  const selectedCategoryId = useHealthEncyclopediaPageStore((s) => s.selectedCategoryId);

  const isCreate = panel?.mode === 'create';
  const isEdit = panel?.mode === 'edit';
  const isOpen = isCreate || isEdit;
  const editingEid = panel?.mode === 'edit' ? panel.eid : null;

  const { data: categoryData } = useCategoriesQuery();
  const categories = categoryData?.categories ?? [];
  const { data: detail, isLoading: isDetailLoading } = useContentDetailQuery(editingEid);

  const createMut = useCreateContent();
  const updateMut = useUpdateContent();
  const deleteMut = useDeleteContent();
  const isPending = createMut.isPending || updateMut.isPending;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<ContentFormErrors>({});
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // SHCT-518 — Save Draft stays available even for a currently-PUBLISHED row:
  // PRD §3.8 supports the 게시중→임시저장 (PUBLISHED→DRAFT) transition (which the BE
  // now allows and which auto-clears homeExposed). Earlier this button was hidden
  // on a misreading of §3.6, which says nothing about restricting draft-revert.

  // Hydrate on open / target change. Create mode pre-selects the currently
  // filtered category (PRD §3.5) when one is active.
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setConfirmPublish(false);
    setConfirmDelete(false);
    if (isEdit) {
      if (detail) setForm(formFromDetail(detail));
    } else {
      setForm({
        ...EMPTY_FORM,
        categoryId: typeof selectedCategoryId === 'number' ? selectedCategoryId : null,
      });
    }
    // selectedCategoryId intentionally read only at open time for create.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, detail]);

  useEffect(() => {
    setIsDirty(false);
    setShowDiscard(false);
  }, [isOpen, isEdit, editingEid]);

  const clearFieldError = (key: ContentFormFieldKey) =>
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const updateTitle = (next: string) => {
    setForm((f) => ({ ...f, title: next.slice(0, TITLE_MAX) }));
    setIsDirty(true);
    clearFieldError('title');
  };

  const updateCategory = (next: number | null) => {
    setForm((f) => ({ ...f, categoryId: next }));
    setIsDirty(true);
    clearFieldError('categoryId');
  };

  const updateThumbnail = (url: string | null) => {
    setForm((f) => ({ ...f, thumbnailUrl: url }));
    setIsDirty(true);
  };

  const addBlock = (type: ContentBlockType) => {
    // ADM.015 — backstop the ≤10 cap (the Add buttons are also disabled at limit).
    setForm((f) => (f.blocks.length >= MAX_BLOCKS ? f : { ...f, blocks: [...f.blocks, buildBlock(type)] }));
    setIsDirty(true);
    clearFieldError('blocks');
  };

  const updateBlock = (id: number, patch: Partial<ContentBlock>) => {
    setForm((f) => ({
      ...f,
      blocks: f.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    }));
    setIsDirty(true);
    clearFieldError('blocks');
  };

  const deleteBlock = (id: number) => {
    setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== id) }));
    setIsDirty(true);
  };

  const moveBlock = (from: number, to: number) => {
    setForm((f) => {
      const next = [...f.blocks];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...f, blocks: next };
    });
    setIsDirty(true);
  };

  // Resolve the selected category's display name for the live preview.
  const selectedCategoryName =
    categories.find((c) => c.id === form.categoryId)?.name ?? null;

  // PRD §1.2 / §7 — CONTENT_LIB:READ users open content as a read-only detail
  // view: every input is locked (the footer's write actions are already hidden
  // when !canWrite). Also locked while an edit's detail is still loading.
  const fieldsDisabled = !canWrite || (isEdit && isDetailLoading);

  const applyApiError = (e: unknown) => {
    const localized = getEncyclopediaErrorMessage(e, t, 'form.toasts.saveError');
    if (e instanceof HealthEncyclopediaApiError && e.fields) {
      const next: ContentFormErrors = {};
      for (const k of Object.keys(e.fields)) {
        if ((FIELD_KEYS as readonly string[]).includes(k)) {
          next[k as ContentFormFieldKey] = localized;
        }
      }
      setErrors((prev) => ({ ...prev, ...next }));
    }
    toast.error(localized);
  };

  const performSave = async (status: ContentStatus) => {
    if (isPending) return;
    try {
      const input = {
        title: form.title.trim(),
        categoryId: form.categoryId as number,
        // SHCT-662 / PRD 10 v2.13 §3.7 — an image block with no upload and a video block
        // with no URL are excluded from the payload, not blocked. This is the same array
        // validateContentForm judged, so the 유효 블록 count it passed is the count the
        // server receives.
        //
        // Video title is optional — send undefined (not '') when blank so the BE
        // 1~50-char rule treats it as absent rather than invalid.
        blocks: withoutEmptyMediaBlocks(form.blocks).map((b) =>
          b.type === 'video' ? { ...b, title: b.title?.trim() ? b.title.trim() : undefined } : b,
        ),
        thumbnailUrl: form.thumbnailUrl,
        status,
      };
      if (isEdit && editingEid) {
        // SHCT-896 — removing the thumbnail needs its own instruction. `thumbnailUrl: null` reaches the
        // server as "leave it unchanged" (a null field and an omitted one are indistinguishable there),
        // so a removal used to be dropped while the save still reported success. Derived from `detail`,
        // the loaded article, rather than a second piece of state that could drift from it: the flag is
        // true exactly when this article HAD a thumbnail and the form no longer does.
        const clearThumbnail = form.thumbnailUrl == null && detail?.thumbnailUrl != null;
        await updateMut.mutateAsync({ eid: editingEid, input: { ...input, clearThumbnail } });
      } else {
        await createMut.mutateAsync(input);
      }
      setIsDirty(false);
      toast.success(status === 'PUBLISHED' ? t('form.toasts.saved') : t('form.toasts.savedDraft'));
      closePanel();
    } catch (e) {
      applyApiError(e);
    }
  };

  const handleSaveClick = (status: ContentStatus) => {
    if (!canWrite || isPending) return;
    const result = validateContentForm(form, t);
    setErrors(result.errors);
    if (!result.ok) return;
    if (status === 'PUBLISHED') {
      setConfirmPublish(true);
      return;
    }
    void performSave('DRAFT');
  };

  const handlePanelClose = () => {
    if (isPending || deleteMut.isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    closePanel();
  };

  // PRD §3.8 — delete from the edit form (Root only). Closes the panel on
  // success; the list refreshes via cache invalidation.
  const handleConfirmDelete = () => {
    if (!editingEid) return;
    deleteMut.mutate(editingEid, {
      onSuccess: () => {
        toast.success(t('deleteContent.success'));
        setConfirmDelete(false);
        closePanel();
      },
      onError: (e) => toast.error(getEncyclopediaErrorMessage(e, t, 'deleteContent.error')),
    });
  };

  const title = isEdit ? t('form.titleEdit') : t('form.titleAdd');
  const eidValue = isEdit ? (editingEid ?? '') : '';

  return (
    <>
      <SlidePanel open={isOpen} title={title} width={880} onClose={handlePanelClose}>
        {/* 2-column on wide panels; collapses to one column on narrow viewports
            so the form + 320px preview never overflow (PRD §5 responsive). */}
        <div className="grid gap-6 grid-cols-[1fr_320px] max-[860px]:grid-cols-1">
          {/* === Left column: form + thumbnail + content blocks === */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {/* EID — read-only; auto-assigned by BE on save (create shows placeholder). */}
          <div className="form-field">
            <label className="form-field__label" htmlFor="encEid">
              {t('form.fields.eid.label')}
            </label>
            <input
              id="encEid"
              type="text"
              className="form-field__input"
              value={eidValue}
              placeholder={t('form.fields.eid.placeholder')}
              disabled
              style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
            />
          </div>

          {/* Title — required, 100-char cap with live counter (PRD §4.3). */}
          <div className="form-field">
            <label className="form-field__label" htmlFor="encTitle">
              {t('form.fields.title.label')} <span className="required">*</span>
            </label>
            <input
              id="encTitle"
              type="text"
              className={`form-field__input${errors.title ? ' form-field__input--error' : ''}`}
              value={form.title}
              maxLength={TITLE_MAX}
              placeholder={t('form.fields.title.placeholder')}
              aria-invalid={errors.title ? 'true' : undefined}
              disabled={fieldsDisabled}
              onChange={(e) => updateTitle(e.target.value)}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <p className="form-field__error" style={{ minHeight: 16 }}>
                {errors.title ?? ''}
              </p>
              <span
                style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4, whiteSpace: 'nowrap' }}
              >
                {form.title.length}/{TITLE_MAX}
              </span>
            </div>
          </div>

          {/* Category — required single select (PRD §4.3). */}
          <div className="form-field">
            <label className="form-field__label" htmlFor="encCategory">
              {t('form.fields.category.label')} <span className="required">*</span>
            </label>
            <select
              id="encCategory"
              className={`form-field__select${errors.categoryId ? ' form-field__input--error' : ''}`}
              value={form.categoryId == null ? '' : String(form.categoryId)}
              aria-invalid={errors.categoryId ? 'true' : undefined}
              disabled={fieldsDisabled}
              onChange={(e) => updateCategory(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="" disabled>{t('form.fields.category.placeholder')}</option>
              {categories.map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name}
                </option>
              ))}
            </select>
            {errors.categoryId ? <p className="form-field__error">{errors.categoryId}</p> : null}
          </div>

          {/* Thumbnail — optional (PRD §4.3). */}
          <div className="form-field">
            <label className="form-field__label">{t('form.fields.thumbnail.label')}</label>
            <EncyclopediaThumbnailUpload
              value={form.thumbnailUrl}
              disabled={fieldsDisabled}
              onChange={updateThumbnail}
            />
          </div>

          {/* Content blocks — required ≥1 with content (PRD §4.3). */}
          <div className="form-field">
            <label className="form-field__label">
              {t('form.fields.blocks.label')} <span className="required">*</span>
            </label>
            <EncyclopediaBlockList
              blocks={form.blocks}
              disabled={fieldsDisabled}
              onAddText={() => addBlock('text')}
              onAddImage={() => addBlock('image')}
              onAddVideo={() => addBlock('video')}
              onChangeBlock={updateBlock}
              onDeleteBlock={deleteBlock}
              onReorder={moveBlock}
            />
            {errors.blocks ? <p className="form-field__error">{errors.blocks}</p> : null}
          </div>
          </div>

          {/* === Right column: patient-app preview === */}
          <div>
            <EncyclopediaMobilePreview
              title={form.title}
              categoryName={selectedCategoryName}
              thumbnailUrl={form.thumbnailUrl}
              blocks={form.blocks}
            />
          </div>
        </div>

        {/* Footer spans full panel width (outside the grid). */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-4)',
            paddingTop: 'var(--space-4)',
            borderTop: '1px solid var(--border)',
          }}
        >
            {/* PRD §3.8 — delete (Root only, edit mode), pushed to the left. */}
            {isEdit && isRoot ? (
              <button
                type="button"
                className="btn btn-default btn-destructive"
                style={{ marginRight: 'auto' }}
                onClick={() => setConfirmDelete(true)}
                disabled={isPending || deleteMut.isPending || isDetailLoading}
              >
                {t('deleteContent.buttonLabel')}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={() => handleSaveClick('DRAFT')}
              disabled={fieldsDisabled || isPending}
              {...denyProps}
            >
              {t('form.actions.saveDraft')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={() => handleSaveClick('PUBLISHED')}
              disabled={fieldsDisabled || isPending}
              {...denyProps}
            >
              {t('form.actions.publish')}
            </button>
        </div>
      </SlidePanel>

      {/* PRD §3.5 — publish confirm. */}
      <ConfirmModal
        open={confirmPublish}
        title={t('publishConfirm.title')}
        message={t('publishConfirm.message')}
        confirmLabel={t('publishConfirm.confirm')}
        cancelLabel={t('publishConfirm.cancel')}
        loading={isPending}
        onConfirm={() => {
          setConfirmPublish(false);
          void performSave('PUBLISHED');
        }}
        onCancel={() => setConfirmPublish(false)}
      />

      {/* PRD §3.8 — delete confirm (edit form, Root only). */}
      <ConfirmModal
        open={confirmDelete}
        title={t('deleteContent.title')}
        message={t('deleteContent.message', { title: form.title })}
        confirmLabel={t('deleteContent.confirm')}
        cancelLabel={t('deleteContent.cancel')}
        confirmVariant="destructive"
        loading={deleteMut.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmModal
        open={showDiscard}
        title={t('discardConfirm.title')}
        message={t('discardConfirm.message')}
        confirmLabel={t('discardConfirm.confirm')}
        cancelLabel={t('discardConfirm.cancel')}
        confirmVariant="destructive"
        onConfirm={() => {
          setShowDiscard(false);
          setIsDirty(false);
          closePanel();
        }}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
}
